import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, MessageSquare, Zap, Users, BarChart3, Bot, Send, ChevronRight, Menu, X } from "lucide-react";

/* ─── Animated counter ──────────────────────────────────────────────────── */
function AnimatedNumber({ target, suffix = "", duration = 2000 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          const start = performance.now();
          const step = (now) => {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(eased * target));
            if (progress < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration]);

  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
}

/* ─── Feature card ──────────────────────────────────────────────────────── */
function FeatureCard({ icon: Icon, title, description, delay = 0 }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="group"
      style={{
        background: "rgba(255,255,255,0.04)",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: "16px",
        padding: "32px 28px",
        transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        transitionDelay: `${delay}ms`,
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(30px)",
        cursor: "default",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "rgba(255,92,53,0.08)";
        e.currentTarget.style.borderColor = "rgba(255,92,53,0.25)";
        e.currentTarget.style.transform = "translateY(-4px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "rgba(255,255,255,0.04)";
        e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: 12,
          background: "linear-gradient(135deg, #FF5C35 0%, #ff7b5a 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 20,
        }}
      >
        <Icon size={22} color="#fff" />
      </div>
      <h3
        style={{
          fontSize: 18,
          fontWeight: 600,
          color: "#fff",
          marginBottom: 10,
          fontFamily: "'Montserrat', sans-serif",
        }}
      >
        {title}
      </h3>
      <p
        style={{
          fontSize: 14,
          lineHeight: 1.7,
          color: "rgba(255,255,255,0.6)",
          margin: 0,
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        {description}
      </p>
    </div>
  );
}

/* ─── Main Landing Page ─────────────────────────────────────────────────── */
export default function Landing() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const features = [
    {
      icon: Send,
      title: "Mass Blasts",
      description: "Send targeted text blasts to thousands of contacts. Filter by group, tag, or custom criteria — then hit send.",
    },
    {
      icon: Zap,
      title: "Drip Campaigns",
      description: "Build automated SMS sequences that nurture leads over days or weeks. Set it and let Relay do the follow-up.",
    },
    {
      icon: Bot,
      title: "AI Auto-Replies",
      description: "Incoming texts get intelligent, instant responses. Your leads stay engaged even when you're showing houses.",
    },
    {
      icon: MessageSquare,
      title: "Two-Way Inbox",
      description: "Real conversations in a unified inbox. See every thread, respond instantly, and never lose context.",
    },
    {
      icon: Users,
      title: "FUB Integration",
      description: "Deep sync with Follow Up Boss. Contacts, tags, action plans — everything flows between Relay and your CRM.",
    },
    {
      icon: BarChart3,
      title: "Delivery Analytics",
      description: "Track sent, delivered, failed, and reply rates across every blast and campaign. Know what's working.",
    },
  ];

  return (
    <div style={{ background: "#0a0f14", minHeight: "100vh", overflowX: "hidden" }}>
      {/* ═══ NAV ═══ */}
      <nav
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          padding: "0 24px",
          height: 72,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: scrolled ? "rgba(10,15,20,0.92)" : "transparent",
          backdropFilter: scrolled ? "blur(20px)" : "none",
          borderBottom: scrolled ? "1px solid rgba(255,255,255,0.06)" : "1px solid transparent",
          transition: "all 0.3s ease",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <img
            src="/relay-logo-white.svg"
            alt="Relay"
            style={{ height: 30 }}
          />
        </div>

        {/* Desktop nav */}
        <div
          style={{ display: "flex", alignItems: "center", gap: 8 }}
          className="hidden md:flex"
        >
          <button
            onClick={() => navigate("/login")}
            style={{
              padding: "10px 22px",
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.15)",
              background: "transparent",
              color: "#fff",
              fontSize: 14,
              fontWeight: 500,
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.3)";
              e.currentTarget.style.background = "rgba(255,255,255,0.05)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)";
              e.currentTarget.style.background = "transparent";
            }}
          >
            Log in
          </button>
          <button
            onClick={() => navigate("/register")}
            style={{
              padding: "10px 22px",
              borderRadius: 10,
              border: "none",
              background: "#FF5C35",
              color: "#fff",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = "#e84e2a"}
            onMouseLeave={(e) => e.currentTarget.style.background = "#FF5C35"}
          >
            Get Started
          </button>
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", padding: 8 }}
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            top: 72,
            zIndex: 99,
            background: "rgba(10,15,20,0.98)",
            backdropFilter: "blur(20px)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 20,
          }}
        >
          <button
            onClick={() => { navigate("/login"); setMobileMenuOpen(false); }}
            style={{
              padding: "14px 40px",
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,0.2)",
              background: "transparent",
              color: "#fff",
              fontSize: 16,
              fontWeight: 500,
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
              width: 220,
            }}
          >
            Log in
          </button>
          <button
            onClick={() => { navigate("/register"); setMobileMenuOpen(false); }}
            style={{
              padding: "14px 40px",
              borderRadius: 12,
              border: "none",
              background: "#FF5C35",
              color: "#fff",
              fontSize: 16,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
              width: 220,
            }}
          >
            Get Started
          </button>
        </div>
      )}

      {/* ═══ HERO ═══ */}
      <section
        style={{
          position: "relative",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          padding: "120px 24px 80px",
          overflow: "hidden",
        }}
      >
        {/* Background glow */}
        <div
          style={{
            position: "absolute",
            top: "-20%",
            left: "50%",
            transform: "translateX(-50%)",
            width: "140%",
            height: "80%",
            background: "radial-gradient(ellipse at center, rgba(255,92,53,0.12) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />

        <div style={{ position: "relative", zIndex: 1, maxWidth: 800 }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 18px",
              borderRadius: 100,
              background: "rgba(255,92,53,0.1)",
              border: "1px solid rgba(255,92,53,0.2)",
              marginBottom: 32,
            }}
          >
            <div
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: "#FF5C35",
                animation: "pulse-dot 2s infinite",
              }}
            />
            <span
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: "#FF5C35",
                fontFamily: "'DM Sans', sans-serif",
                letterSpacing: "0.02em",
              }}
            >
              Built for real estate teams on FUB
            </span>
          </div>

          <h1
            style={{
              fontSize: "clamp(36px, 6vw, 64px)",
              fontWeight: 700,
              color: "#fff",
              lineHeight: 1.1,
              fontFamily: "'Montserrat', sans-serif",
              letterSpacing: "-0.02em",
              margin: "0 0 24px",
            }}
          >
            Every lead.{" "}
            <span
              style={{
                background: "linear-gradient(135deg, #FF5C35 0%, #ff8866 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Right on time.
            </span>
          </h1>

          <p
            style={{
              fontSize: "clamp(16px, 2.2vw, 20px)",
              lineHeight: 1.7,
              color: "rgba(255,255,255,0.55)",
              maxWidth: 560,
              margin: "0 auto 40px",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            Relay is the SMS platform built for teams that run on Follow Up Boss.
            Blasts, drip campaigns, AI replies — all synced to your CRM.
          </p>

          <div
            style={{
              display: "flex",
              gap: 14,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={() => navigate("/register")}
              style={{
                padding: "16px 36px",
                borderRadius: 12,
                border: "none",
                background: "#FF5C35",
                color: "#fff",
                fontSize: 16,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "'DM Sans', sans-serif",
                display: "flex",
                alignItems: "center",
                gap: 10,
                transition: "all 0.25s",
                boxShadow: "0 0 40px rgba(255,92,53,0.3)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#e84e2a";
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 0 60px rgba(255,92,53,0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#FF5C35";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 0 40px rgba(255,92,53,0.3)";
              }}
            >
              Start free trial <ArrowRight size={18} />
            </button>
            <button
              onClick={() => navigate("/login")}
              style={{
                padding: "16px 36px",
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.12)",
                background: "rgba(255,255,255,0.04)",
                color: "rgba(255,255,255,0.8)",
                fontSize: 16,
                fontWeight: 500,
                cursor: "pointer",
                fontFamily: "'DM Sans', sans-serif",
                transition: "all 0.25s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.25)";
                e.currentTarget.style.background = "rgba(255,255,255,0.08)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)";
                e.currentTarget.style.background = "rgba(255,255,255,0.04)";
              }}
            >
              Sign in
            </button>
          </div>

          {/* Stats row */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "clamp(32px, 6vw, 64px)",
              marginTop: 72,
              flexWrap: "wrap",
            }}
          >
            {[
              { value: 50000, suffix: "+", label: "Messages sent" },
              { value: 98, suffix: "%", label: "Delivery rate" },
              { value: 500, suffix: "+", label: "Agents active" },
            ].map(({ value, suffix, label }) => (
              <div key={label} style={{ textAlign: "center" }}>
                <div
                  style={{
                    fontSize: "clamp(28px, 4vw, 40px)",
                    fontWeight: 700,
                    color: "#fff",
                    fontFamily: "'Montserrat', sans-serif",
                    letterSpacing: "-0.02em",
                  }}
                >
                  <AnimatedNumber target={value} suffix={suffix} />
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: "rgba(255,255,255,0.4)",
                    marginTop: 4,
                    fontFamily: "'DM Sans', sans-serif",
                    fontWeight: 500,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                  }}
                >
                  {label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ FEATURES ═══ */}
      <section
        style={{
          padding: "80px 24px 100px",
          maxWidth: 1200,
          margin: "0 auto",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: 60 }}>
          <h2
            style={{
              fontSize: "clamp(28px, 4vw, 40px)",
              fontWeight: 700,
              color: "#fff",
              fontFamily: "'Montserrat', sans-serif",
              letterSpacing: "-0.02em",
              margin: "0 0 16px",
            }}
          >
            Everything you need to{" "}
            <span style={{ color: "#FF5C35" }}>close faster</span>
          </h2>
          <p
            style={{
              fontSize: 16,
              color: "rgba(255,255,255,0.5)",
              maxWidth: 500,
              margin: "0 auto",
              fontFamily: "'DM Sans', sans-serif",
              lineHeight: 1.6,
            }}
          >
            One platform for SMS outreach, automation, and lead engagement — plugged directly into your FUB workflow.
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: 20,
          }}
        >
          {features.map((feature, i) => (
            <FeatureCard key={feature.title} {...feature} delay={i * 80} />
          ))}
        </div>
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <section
        style={{
          padding: "80px 24px 100px",
          background: "linear-gradient(180deg, rgba(255,92,53,0.04) 0%, transparent 100%)",
        }}
      >
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <h2
            style={{
              fontSize: "clamp(28px, 4vw, 40px)",
              fontWeight: 700,
              color: "#fff",
              fontFamily: "'Montserrat', sans-serif",
              letterSpacing: "-0.02em",
              textAlign: "center",
              margin: "0 0 60px",
            }}
          >
            Up and running in <span style={{ color: "#FF5C35" }}>minutes</span>
          </h2>

          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {[
              {
                step: "01",
                title: "Connect your FUB account",
                desc: "Paste your API key, and Relay syncs your contacts, tags, and pipeline stages automatically.",
              },
              {
                step: "02",
                title: "Set up your SMS number",
                desc: "Bring your own SignalWire number or provision a new one. 10DLC registration handled.",
              },
              {
                step: "03",
                title: "Send your first blast",
                desc: "Pick a group, write your message (or let AI draft it), and hit send. Track delivery in real-time.",
              },
            ].map(({ step, title, desc }, i) => (
              <div
                key={step}
                style={{
                  display: "flex",
                  gap: 28,
                  alignItems: "flex-start",
                  padding: "32px 0",
                  borderBottom: i < 2 ? "1px solid rgba(255,255,255,0.06)" : "none",
                }}
              >
                <div
                  style={{
                    fontSize: 48,
                    fontWeight: 800,
                    color: "rgba(255,92,53,0.15)",
                    fontFamily: "'Montserrat', sans-serif",
                    lineHeight: 1,
                    minWidth: 72,
                    flexShrink: 0,
                  }}
                >
                  {step}
                </div>
                <div>
                  <h3
                    style={{
                      fontSize: 20,
                      fontWeight: 600,
                      color: "#fff",
                      margin: "0 0 8px",
                      fontFamily: "'Montserrat', sans-serif",
                    }}
                  >
                    {title}
                  </h3>
                  <p
                    style={{
                      fontSize: 15,
                      lineHeight: 1.7,
                      color: "rgba(255,255,255,0.5)",
                      margin: 0,
                      fontFamily: "'DM Sans', sans-serif",
                    }}
                  >
                    {desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section style={{ padding: "80px 24px 100px" }}>
        <div
          style={{
            maxWidth: 800,
            margin: "0 auto",
            textAlign: "center",
            padding: "64px 40px",
            borderRadius: 24,
            background: "linear-gradient(135deg, rgba(255,92,53,0.1) 0%, rgba(255,92,53,0.04) 100%)",
            border: "1px solid rgba(255,92,53,0.15)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "-50%",
              right: "-20%",
              width: "60%",
              height: "200%",
              background: "radial-gradient(circle, rgba(255,92,53,0.08) 0%, transparent 60%)",
              pointerEvents: "none",
            }}
          />
          <div style={{ position: "relative", zIndex: 1 }}>
            <h2
              style={{
                fontSize: "clamp(24px, 4vw, 36px)",
                fontWeight: 700,
                color: "#fff",
                fontFamily: "'Montserrat', sans-serif",
                letterSpacing: "-0.02em",
                margin: "0 0 16px",
              }}
            >
              Ready to relay?
            </h2>
            <p
              style={{
                fontSize: 16,
                color: "rgba(255,255,255,0.5)",
                maxWidth: 440,
                margin: "0 auto 32px",
                fontFamily: "'DM Sans', sans-serif",
                lineHeight: 1.6,
              }}
            >
              Join hundreds of real estate teams already using Relay to turn leads into closings.
            </p>
            <button
              onClick={() => navigate("/register")}
              style={{
                padding: "16px 40px",
                borderRadius: 12,
                border: "none",
                background: "#FF5C35",
                color: "#fff",
                fontSize: 16,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "'DM Sans', sans-serif",
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                transition: "all 0.25s",
                boxShadow: "0 0 40px rgba(255,92,53,0.3)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#e84e2a";
                e.currentTarget.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#FF5C35";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              Get started now <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer
        style={{
          padding: "40px 24px",
          borderTop: "1px solid rgba(255,255,255,0.06)",
          maxWidth: 1200,
          margin: "0 auto",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 16,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <img src="/relay-logo-white.svg" alt="Relay" style={{ height: 22, opacity: 0.5 }} />
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 24,
            fontSize: 13,
            color: "rgba(255,255,255,0.35)",
            fontFamily: "'DM Sans', sans-serif",
          }}
        >
          <a
            href="/privacy"
            style={{ color: "inherit", textDecoration: "none", transition: "color 0.2s" }}
            onMouseEnter={(e) => e.currentTarget.style.color = "rgba(255,255,255,0.7)"}
            onMouseLeave={(e) => e.currentTarget.style.color = "rgba(255,255,255,0.35)"}
          >
            Privacy
          </a>
          <span>&copy; {new Date().getFullYear()} MVSS Studio</span>
        </div>
      </footer>

      {/* Keyframes */}
      <style>{`
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.5); }
        }
      `}</style>
    </div>
  );
}
